/**
 * Bridge to the native iOS app (WKWebView message handlers).
 * Only available when running inside the Jetsnode iOS app (isInAppWebView()).
 */

export interface NativePreferences {
  useFaceId: boolean;
}

declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        nativePreferences?: { postMessage: (payload: unknown) => void };
      };
    };
    __onNativePreferences?: (prefs: NativePreferences) => void;
    /** Called by native when a preference was rejected (e.g. Face ID failed when turning on). */
    __onNativePreferenceRejected?: (key: keyof NativePreferences) => void;
  }
}

const handlerName = 'nativePreferences';

function isAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.webkit?.messageHandlers?.[handlerName];
}

/**
 * Request current native preferences. The native app will call window.__onNativePreferences(prefs) with the result.
 */
export function getNativePreferences(callback: (prefs: NativePreferences) => void): void {
  if (!isAvailable()) return;
  window.__onNativePreferences = callback;
  window.webkit!.messageHandlers![handlerName].postMessage({ action: 'getPreferences' });
}

/**
 * Set a native preference (stored in UserDefaults on the device).
 */
export function setNativePreference(key: keyof NativePreferences, value: boolean): void {
  if (!isAvailable()) return;
  window.webkit!.messageHandlers![handlerName].postMessage({
    action: 'setPreference',
    key,
    value,
  });
}

export function isNativeBridgeAvailable(): boolean {
  return isAvailable();
}
